import { lerpPt, rot, type Vec2 } from "../systems/math";

/** Port of leaf-fractal.js leaflet / home-point construction. */
const leaflets = [
  { angle: 0, length: 1, width: 0.2, peak: 0.42, center: true },
  { angle: 0.73, length: 0.86, width: 0.175, peak: 0.42, center: false },
  { angle: 1.36, length: 0.64, width: 0.15, peak: 0.4, center: false },
  { angle: 2.18, length: 0.4, width: 0.12, peak: 0.38, center: false },
] as const;

const stem = { top: 0, bottom: -0.26, halfW: 0.026 };
const edgeDiv = 3;

const kite = (leaflet: (typeof leaflets)[number]) => ({
  hub: rot(0, 0, leaflet.angle),
  right: rot(leaflet.width, leaflet.length * leaflet.peak, leaflet.angle),
  tip: rot(0, leaflet.length, leaflet.angle),
  left: rot(-leaflet.width, leaflet.length * leaflet.peak, leaflet.angle),
});

export type LeafHome = {
  x: number;
  y: number;
  role: string;
  leaflet: number;
};

export function buildLeafHomes(): LeafHome[] {
  const points: LeafHome[] = [];
  const push = (p: Vec2, role: string, leaflet = 0) =>
    points.push({ x: Math.max(0, p.x), y: p.y, role, leaflet });

  push({ x: 0, y: 0 }, "hub", -1);

  leaflets.forEach((leaflet, index) => {
    const shape = kite(leaflet);
    if (leaflet.center) {
      for (let s = 1; s <= edgeDiv; s += 1) {
        push(lerpPt(shape.hub, shape.right, s / (edgeDiv + 1)), "edge", index);
      }
      push(shape.right, "waist", index);
      for (let s = 1; s <= edgeDiv; s += 1) {
        push(lerpPt(shape.right, shape.tip, s / (edgeDiv + 1)), "edge", index);
      }
      push(shape.tip, "tip", index);
      for (let s = 1; s <= edgeDiv + 1; s += 1) {
        push(lerpPt(shape.hub, shape.tip, s / (edgeDiv + 2)), "vein", index);
      }
      for (let s = 1; s <= edgeDiv; s += 1) {
        const p = lerpPt(shape.tip, shape.left, s / (edgeDiv + 1));
        push({ x: Math.abs(p.x) * 0.15, y: p.y }, "edge", index);
      }
    } else {
      for (let s = 1; s <= edgeDiv; s += 1) {
        push(lerpPt(shape.hub, shape.right, s / (edgeDiv + 1)), "edge", index);
      }
      push(shape.right, "waist", index);
      for (let s = 1; s <= edgeDiv; s += 1) {
        push(lerpPt(shape.right, shape.tip, s / (edgeDiv + 1)), "edge", index);
      }
      push(shape.tip, "tip", index);
      for (let s = 1; s <= edgeDiv; s += 1) {
        const p = lerpPt(shape.tip, shape.left, s / (edgeDiv + 1));
        push(
          p.x >= 0
            ? p
            : rot(leaflet.width * 0.4, leaflet.length * (0.5 + s * 0.08), leaflet.angle),
          "edge",
          index,
        );
      }
      for (let s = 1; s <= edgeDiv + 1; s += 1) {
        push(lerpPt(shape.hub, shape.tip, s / (edgeDiv + 2)), "vein", index);
      }
    }
  });

  push({ x: stem.halfW, y: stem.top }, "stem", -1);
  push({ x: stem.halfW, y: stem.bottom * 0.45 }, "stem", -1);
  push({ x: stem.halfW * 0.7, y: stem.bottom }, "stem", -1);
  push({ x: 0, y: stem.bottom }, "stem", -1);

  // Mirror to full leaf for 3D scatter
  const mirrored = points
    .filter((p) => p.x > 0.001)
    .map((p) => ({ ...p, x: -p.x }));

  return [...points, ...mirrored];
}

export { leaflets };
